import React, { useContext, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '../../../../assets/icons/arrow-back.svg?url';
import BetterflyLogoSmall from '../../../../assets/icons/betterfly-logo-small.svg?url';
import { ReactComponent as ExpandLess } from '../../../../assets/icons/expand_less.svg';
import { ReactComponent as ExpandMore } from '../../../../assets/icons/expand_more.svg';
import { ReactComponent as ClockIcon } from '../../../../assets/icons/v3/clock.svg';
import SidebarShape from '../../../../assets/images/sidebar-shape.svg?url';
import {
  NAVBAR_MENU_ITEMS_EMPTY_COMPANY,
  NAVBAR_MENU_ITEMS_NE,
  NAVBAR_MENU_ITEMS_RF
} from '../../../constants/navigation';
import { PERMISSIONS_BY_ROUTE } from '../../../constants/permissions';
import { PLAN_TYPE_FR, PLAN_TYPE_HEALTH } from '../../../constants/plan';
import {
  CAN_SEE_METRICS_AND_REPORTS,
  CAN_SEE_REWARDS,
  CAN_SEE_STORIES,
  CAN_SEE_REQUESTS_FG
} from '../../../constants/statsig';
import { CLICK_ACTION, SIDEBAR_SECTION } from '../../../constants/tagManager';
import { SessionContext } from '../../../context/auth/authContext';
import { CompanyContext } from '../../../context/company/companyContext';
import { PlansContext } from '../../../context/plans/plansContext';
import { useFeatureGate } from '../../../hooks/useFeatureGate/useFeatureGate';
import usePermissions from '../../../hooks/usePermissions/usePermissions';
import { INavbarMenuItem } from '../../../interfaces/appNavbar';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';

import './Sidebar.scss';

interface SidebarProps {
  openMenuTablet: boolean;
  closeMenuTablet: () => void;
}

const Sidebar = ({ openMenuTablet, closeMenuTablet }: SidebarProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<Record<number, boolean>>({});

  const { isSpain, companyData, companyPlansType } = useContext(CompanyContext);
  const { plans } = useContext(PlansContext);

  const { isBetterflyer } = useContext(SessionContext);
  const { hasAnyAccess } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isEnabled: requestsGateEnabled } = useFeatureGate(CAN_SEE_REQUESTS_FG);
  const { isEnabled: canSeeMetrics } = useFeatureGate(CAN_SEE_METRICS_AND_REPORTS);
  const { isEnabled: canSeeRewards } = useFeatureGate(CAN_SEE_REWARDS);
  const { isEnabled: canSeeStories } = useFeatureGate(CAN_SEE_STORIES);
  const hasRequestsSection = plans.some((p) => p.supportRequests);

  const hasFeatureGateAccess = useMemo(() => {
    return requestsGateEnabled && companyPlansType === PLAN_TYPE_FR && hasRequestsSection;
  }, [requestsGateEnabled, companyPlansType, hasRequestsSection]);

  const isEmptyCompany = useMemo(() => {
    return !isBetterflyer && !companyData?.hasMembers && companyPlansType === PLAN_TYPE_HEALTH;
  }, [isBetterflyer, companyData?.hasMembers, companyPlansType]);

  const menuItems = useMemo(() => {
    let baseItems = [];

    if (hasFeatureGateAccess) {
      baseItems = [...NAVBAR_MENU_ITEMS_RF];
    } else {
      baseItems = [...NAVBAR_MENU_ITEMS_NE];
    }

    if (isEmptyCompany) {
      return NAVBAR_MENU_ITEMS_EMPTY_COMPANY;
    }

    const processedItems = baseItems.map((item) => {
      if (item.id === 7) {
        return {
          ...item,
          title: isSpain ? TRANSLATIONS_GLOBAL.sidebar.metrics.rf : TRANSLATIONS_GLOBAL.sidebar.metrics.default
        };
      }
      return item;
    });

    const gatedItems = processedItems.filter((item) => {
      if (item.id === 7) return canSeeMetrics && hasAnyAccess(item.permissionsRequired);
      if (item.id === 8) return canSeeRewards && hasAnyAccess(item.permissionsRequired);
      if (item.id === 12) return canSeeStories && hasAnyAccess(item.permissionsRequired);
      return hasAnyAccess(item.permissionsRequired);
    });

    return gatedItems.sort((a, b) => a.id - b.id);
  }, [hasFeatureGateAccess, isEmptyCompany, isSpain, hasAnyAccess, canSeeMetrics, canSeeRewards, canSeeStories]);

  const handleDesktopMenu = (status: boolean): void => {
    setOpenMenu(status);
  };

  const handleKeyDownToAction = (e: React.KeyboardEvent<HTMLDivElement>, item: INavbarMenuItem) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleAction(item);
    }
  };

  const isActive = (item: INavbarMenuItem): boolean => {
    if (item.actionPath !== undefined) {
      return item.actionPath.includes(location.pathname);
    } else if (item.submenu) {
      return item?.submenu.items.some((subItem) => subItem?.actionPath?.includes(location.pathname));
    }
    return false;
  };

  const isSubmenuOpen = (itemId: number): boolean => {
    return openSubmenus[itemId] || false;
  };

  const handleAction = (item: INavbarMenuItem) => {
    if (item.actionPath !== undefined) {
      for (const pathName of item.actionPath) {
        if (hasAnyAccess(PERMISSIONS_BY_ROUTE[pathName])) {
          setOpenMenu(false);
          closeMenuTablet();
          TagManagerService.pushEvent(CLICK_ACTION, SIDEBAR_SECTION, item.tagManagerSection);
          return navigate(pathName);
        }
      }
    } else if (item.submenu) {
      setOpenSubmenus((prev) => ({
        ...prev,
        [item.id]: !prev[item.id]
      }));
    }
  };

  const emptyCompanyCard = () => {
    if (!isEmptyCompany) {
      return null;
    }
    return (
      <div className={`empty-company-card ${openMenu ? 'open' : ''}`}>
        <div className="empty-company-card-container">
          <ClockIcon className="empty-company-card-icon" />
          <div className="empty-company-card-title">{t(TRANSLATIONS_GLOBAL.sidebar.emptyCompanyCard.title)}</div>
          <div className="empty-company-card-description">
            {t(TRANSLATIONS_GLOBAL.sidebar.emptyCompanyCard.description)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="app-sidebar-container" data-experience="new">
        <button
          className={`desktop-sidebar ${openMenu ? 'open' : ''}`}
          onMouseEnter={() => handleDesktopMenu(true)}
          onMouseLeave={() => handleDesktopMenu(false)}
        >
          <div className={`menu-header ${openMenu ? 'open' : ''}`}>
            <img
              className={`betterfly-logo ${openMenu ? '' : 'small'}`}
              src={BetterflyLogoSmall}
              alt={`Betterfly logo ${openMenu ? 'open' : 'close'}`}
            />
          </div>
          <div className="menu-list">
            {menuItems?.map((item) => (
              <span key={`desktop-${item.id}`} className="w-full">
                <div
                  className={`menu-item ${isActive(item) ? 'active' : ''} ${openMenu ? 'open' : ''} ${
                    openMenu && isSubmenuOpen(item.id) ? 'submenu-open' : ''
                  }`}
                  onClick={() => handleAction(item)}
                  onKeyDown={(e) => handleKeyDownToAction(e, item)}
                  tabIndex={0}
                >
                  <span>
                    <item.iconComponent
                      className={item.id == 1 ? 'sidebar-icon-home' : 'sidebar-icon'}
                      title={`item-img-${item.id}`}
                    />

                    {openMenu && t(item.title)}
                  </span>
                  {openMenu &&
                    item?.submenu &&
                    (isSubmenuOpen(item.id) ? <ExpandLess className="chevron" /> : <ExpandMore className="chevron" />)}
                </div>
                {openMenu && item?.submenu && isSubmenuOpen(item.id) && (
                  <div className={`submenu ${openMenu ? 'active' : ''}`}>
                    {item.submenu.items.map((subItem) => (
                      <div
                        key={`desktop-${subItem.id}`}
                        className={`menu-item ${isActive(subItem as INavbarMenuItem) ? 'active' : ''}`}
                        onClick={() => handleAction(subItem as INavbarMenuItem)}
                        onKeyDown={(e) => handleKeyDownToAction(e, item)}
                        tabIndex={0}
                      >
                        {t(subItem.title)}
                      </div>
                    ))}
                  </div>
                )}
              </span>
            ))}
          </div>
          {emptyCompanyCard()}
        </button>
        <div className={`tablet-sidebar ${openMenuTablet ? 'open' : ''}`}>
          <div className="menu-header">
            <button
              className="menu-item"
              onClick={closeMenuTablet}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  closeMenuTablet();
                }
              }}
            >
              <img className="arrow-back" src={ArrowBackIcon} alt="Arrow back" />
            </button>
            <img className="betterfly-logo" src={BetterflyLogoSmall} alt="Betterfly logo tablet" />
          </div>
          <div className="menu-list">
            {menuItems?.map(
              (item) =>
                openMenuTablet && (
                  <span key={`tablet-${item.id}`} className="w-full">
                    <div
                      className={`menu-item ${!item?.submenu && isActive(item) && 'active'}`}
                      onClick={() => handleAction(item)}
                      tabIndex={0}
                      onKeyDown={(e) => handleKeyDownToAction(e, item)}
                    >
                      {item.iconComponent && (
                        <item.iconComponent className="menu-item-icon sidebar-icon" title={`Item ${item.id} mobile`} />
                      )}
                      {openMenuTablet && t(item.title)}
                    </div>
                    {item?.submenu && (
                      <div className="submenu">
                        {item?.submenu.items.map((subItem) => (
                          <div
                            key={`tablet-${subItem.id}`}
                            className={`menu-item ${isActive(subItem as INavbarMenuItem) && 'active'}`}
                            onClick={() => handleAction(subItem as INavbarMenuItem)}
                            onKeyDown={(e) => handleKeyDownToAction(e, item)}
                            tabIndex={0}
                          >
                            {openMenuTablet && t(subItem.title)}
                          </div>
                        ))}
                      </div>
                    )}
                  </span>
                )
            )}
          </div>
        </div>
      </div>
      <div className={`app-sidebar-shape ${openMenu ? 'open' : ''}`}>
        <img src={SidebarShape} alt="shape" />
      </div>
    </>
  );
};

export default Sidebar;
